import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import PromotionContext from "~/contexts/PromotionContextProvider";
import OrderContext from "~/contexts/OrderContext";
import MenuContext from "~/contexts/MenuContext";
import ModalContext from "~/contexts/ModalContext";
import { CartLine } from "~/entities/orderEntities";
import {
  Deal,
  DealPicks,
  emptyPicks,
  findPartialDeal,
  getDeals,
  PartialDeal,
  picksFromLines,
} from "~/helpers/dealHelpers";
import { DealGroup, groupCartLines } from "~/helpers/bundleDisplay";
import { lineInBundle } from "~/helpers/promotionHelpers";
import { appliedOfferNames } from "~/helpers/offerAmounts";
import DealBuilder from "~/modules/deals/containers/DealBuilder/DealBuilder";

// What changed about the customer's deals in the last save, shown where the change happened
export type DealNotice =
  | { kind: "gained"; name: string; saving: number }
  // A lone free item added straight to the order for an offer, which can be undone
  | { kind: "freeAdded"; name: string; lineIds: string[] }
  | {
      kind: "lost";
      promotionCode: string;
      name: string;
      reason: string;
      // Items from the deal still in the cart, and those that were taken out
      remainingLines: CartLine[];
      removedLines: CartLine[];
    }
  | {
      // An offer other than a deal stopped applying after the time, order type or items changed
      kind: "offerLost";
      name: string;
      cause: "time" | "orderType" | "cart";
      requestedReadyTime: string | null;
      increase: number;
    };

interface DealBuilderContextValue {
  // Bundle deals that can be built from the current menu
  deals: Deal[];
  // Start building a deal, optionally with a line already chosen for one slot
  openDealBuilder: (
    promotionCode: string,
    prefill?: { slotIndex: number; line: CartLine },
  ) => void;
  // Edit a deal already in the cart, replacing its lines when saved. With swapLineId that item is taken out so its
  // slot is chosen again.
  openDealEditor: (
    promotionCode: string,
    lines: CartLine[],
    swapLineId?: string,
  ) => void;
  // The deal loose items are one short of, unless dismissed
  partialDeal: PartialDeal | null;
  dismissPartialDeal: VoidFunction;
  dealNotice: DealNotice | null;
  dismissDealNotice: VoidFunction;
  // "Free Dog added to your order", shown once the added lines are in, until the next cart change
  showFreeItemAdded: (name: string, lineIds: string[]) => void;
}

const DealBuilderContext = createContext<DealBuilderContextValue>({
  deals: [],
  openDealBuilder: () => null,
  openDealEditor: () => null,
  partialDeal: null,
  dismissPartialDeal: () => null,
  dealNotice: null,
  dismissDealNotice: () => null,
  showFreeItemAdded: () => null,
});

const GAINED_NOTICE_MS = 4000;

// Lines that could still go into a deal
const looseLines = (lines: CartLine[]) =>
  lines.filter(
    (x) =>
      !lineInBundle(x) &&
      x.item.qty > 0 &&
      [...x.discounts, ...x.item.discounts].every((d) => d.promotionCode),
  );

const namesOf = (lines: CartLine[]) => {
  const names = lines.map((x) => x.item.product.name);
  return names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

export const DealBuilderProvider = ({ children }: PropsWithChildren) => {
  const { scheduledPromotions } = useContext(PromotionContext);
  const { orderSummary, orderIdle } = useContext(OrderContext);
  const { menu } = useContext(MenuContext);

  const deals = useMemo(
    () =>
      getDeals(
        [...scheduledPromotions, ...(orderSummary.unlockedPromotions ?? [])],
        menu,
      ),
    [scheduledPromotions, orderSummary.unlockedPromotions, menu],
  );

  const [open, setOpen] = useState<{
    deal: Deal;
    picks: DealPicks;
    replaceLineIds: string[] | null;
    isEdit: boolean;
  } | null>(null);

  const openDealBuilder: DealBuilderContextValue["openDealBuilder"] = (
    promotionCode,
    prefill,
  ) => {
    const deal = deals.find((x) => x.code === promotionCode);
    if (!deal) {
      return;
    }
    const picks = emptyPicks(deal);
    if (prefill) {
      picks[prefill.slotIndex] = [prefill.line];
    }
    setOpen({ deal, picks, replaceLineIds: null, isEdit: false });
  };

  const openDealEditor: DealBuilderContextValue["openDealEditor"] = (
    promotionCode,
    lines,
    swapLineId,
  ) => {
    const deal = deals.find((x) => x.code === promotionCode);
    if (!deal) {
      return;
    }
    const picks = picksFromLines(deal, lines).map((slotLines) =>
      slotLines.filter((x) => x.id !== swapLineId),
    );
    setOpen({
      deal,
      picks,
      replaceLineIds: lines.map((x) => x.id),
      isEdit: true,
    });
  };

  // A promo code or reward that unlocks a deal opens the builder straight away, starting from any loose single
  // items already in the cart that fit it
  const { setShowAssetModal } = useContext(ModalContext);
  const unlockedBundles = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!orderIdle) {
      return;
    }
    const codes = new Set(
      (orderSummary.unlockedPromotions ?? [])
        .filter((x) => x.bundleComponents?.length)
        .map((x) => x.code),
    );
    const prev = unlockedBundles.current;
    unlockedBundles.current = codes;
    if (!prev) {
      return;
    }
    const code = [...codes].find((x) => !prev.has(x));
    const deal = code && deals.find((x) => x.code === code);
    const alreadyInCart = groupCartLines(orderSummary.lines).some(
      (x) => x.type === "deal" && x.deal.promotionCode === code,
    );
    if (!deal || alreadyInCart) {
      return;
    }
    const picks = picksFromLines(
      deal,
      looseLines(orderSummary.lines).filter((x) => x.item.qty === 1),
    );
    const used = picks.flat().map((x) => x.id);
    setShowAssetModal(false);
    setOpen({
      deal,
      picks,
      replaceLineIds: used.length ? used : null,
      isEdit: false,
    });
  }, [
    orderSummary.unlockedPromotions,
    orderSummary.lines,
    orderIdle,
    deals,
    setShowAssetModal,
  ]);

  // Deal changes between one settled order and the next
  const [dealNotice, setDealNotice] = useState<DealNotice | null>(null);
  // A free item notice waiting for its lines to land, so the save that adds them doesn't clear it
  const pendingFreeAdded = useRef<DealNotice | null>(null);
  const showFreeItemAdded = useCallback((name: string, lineIds: string[]) => {
    pendingFreeAdded.current = { kind: "freeAdded", name, lineIds };
  }, []);
  const previous = useRef<{
    groups: Record<string, DealGroup>;
    lineIds: string;
    offers: Record<string, string>;
    requestedReadyTime: string | null;
    orderTypeCode: string;
    promoCode: string | null;
    redemptionKey: string | null;
    unlocked: Set<string>;
    total: number;
  } | null>(null);
  useEffect(() => {
    if (!orderIdle) {
      return;
    }
    const groups: Record<string, DealGroup> = {};
    for (const entry of groupCartLines(orderSummary.lines)) {
      if (entry.type === "deal") {
        groups[entry.deal.instanceId] = entry.deal;
      }
    }
    const lineIds = orderSummary.lines
      .map((x) => `${x.id}:${x.item.qty}`)
      .join(",");
    const current = {
      groups,
      lineIds,
      offers: appliedOfferNames(orderSummary),
      requestedReadyTime: orderSummary.requestedReadyTime,
      orderTypeCode: orderSummary.orderTypeCode,
      promoCode: orderSummary.promoCode,
      redemptionKey: orderSummary.redemptionKey,
      unlocked: new Set(
        (orderSummary.unlockedPromotions ?? []).map((x) => x.code),
      ),
      total: orderSummary.finalPriceIncTax,
    };
    const prev = previous.current;
    previous.current = current;
    const timeChanged = prev?.requestedReadyTime !== current.requestedReadyTime;
    const typeChanged = prev?.orderTypeCode !== current.orderTypeCode;
    if (!prev || (prev.lineIds === lineIds && !timeChanged && !typeChanged)) {
      return;
    }

    const freeAdded = pendingFreeAdded.current;
    if (freeAdded?.kind === "freeAdded") {
      pendingFreeAdded.current = null;
      const ids = new Set(orderSummary.lines.map((x) => x.id));
      if (freeAdded.lineIds.every((x) => ids.has(x))) {
        setDealNotice(freeAdded);
        return;
      }
    }

    // Offers (other than deals) that stopped applying, unless they came from a code or reward that was taken off
    const codeOrRewardRemoved =
      prev.promoCode !== current.promoCode ||
      prev.redemptionKey !== current.redemptionKey;
    const lostOffer = Object.entries(prev.offers).find(
      ([code]) =>
        !current.offers[code] &&
        !(codeOrRewardRemoved && prev.unlocked.has(code)),
    );

    const currentIds = new Set(orderSummary.lines.map((x) => x.id));
    let lost: DealNotice | null = null;
    for (const [id, group] of Object.entries(prev.groups)) {
      if (groups[id]) {
        continue;
      }
      const remainingLines = orderSummary.lines.filter((x) =>
        group.members.some((m) => m.line.id === x.id),
      );
      // The whole deal was removed or replaced on purpose, or the server re-formed it from other items
      if (!remainingLines.length || remainingLines.some(lineInBundle)) {
        continue;
      }
      const removedLines = group.members
        .map((m) => m.line)
        .filter((x) => !currentIds.has(x.id));
      lost = {
        kind: "lost",
        promotionCode: group.promotionCode,
        name: group.name,
        reason: removedLines.length
          ? `You removed the ${namesOf(removedLines)}.`
          : "It no longer applies to this order.",
        remainingLines,
        removedLines,
      };
    }
    if (lost) {
      setDealNotice(lost);
      return;
    }
    if (lostOffer) {
      setDealNotice({
        kind: "offerLost",
        name: lostOffer[1],
        cause: timeChanged ? "time" : typeChanged ? "orderType" : "cart",
        requestedReadyTime: current.requestedReadyTime,
        increase: Math.max(0, current.total - prev.total),
      });
      return;
    }

    const gained = Object.entries(groups).find(([id]) => !prev.groups[id]);
    setDealNotice(
      gained
        ? { kind: "gained", name: gained[1].name, saving: gained[1].saving }
        : // A lost notice stays until the next cart change
          null,
    );
  }, [orderSummary, orderIdle]);

  useEffect(() => {
    if (dealNotice?.kind !== "gained") {
      return;
    }
    const timeout = setTimeout(() => setDealNotice(null), GAINED_NOTICE_MS);
    return () => clearTimeout(timeout);
  }, [dealNotice]);

  // "Add a drink to make it a Burger Combo", until dismissed for this set of items
  const partial = useMemo(
    () => findPartialDeal(deals, looseLines(orderSummary.lines)),
    [deals, orderSummary.lines],
  );
  const partialKey = partial
    ? `${partial.deal.code}:${partial.countedLineIds.join(",")}`
    : null;
  const [dismissedPartial, setDismissedPartial] = useState<string | null>(null);
  const partialDeal =
    partial &&
    partialKey !== dismissedPartial &&
    // The lost notice already offers the way back to this deal
    !(
      dealNotice?.kind === "lost" &&
      dealNotice.promotionCode === partial.deal.code
    )
      ? partial
      : null;

  return (
    <DealBuilderContext.Provider
      value={{
        deals,
        openDealBuilder,
        openDealEditor,
        partialDeal,
        dismissPartialDeal: () => setDismissedPartial(partialKey),
        dealNotice,
        dismissDealNotice: () => setDealNotice(null),
        showFreeItemAdded,
      }}
    >
      {children}
      {open && (
        <DealBuilder
          key={open.deal.code}
          deal={open.deal}
          initialPicks={open.picks}
          replaceLineIds={open.replaceLineIds}
          isEdit={open.isEdit}
          onClose={() => setOpen(null)}
        />
      )}
    </DealBuilderContext.Provider>
  );
};

export default DealBuilderContext;
